import { capitalize } from 'lodash';
import { MinusCircle, PlusCircle } from 'lucide-react';
import { Label, Badge, Collapsible, CollapsibleContent, CollapsibleTrigger, Row, Col, ButtonIcon } from 'twyne-new.ui';

import CodeEditor from '@/components/CodeEditor';
import { useCollapse } from '@/components/Grid';
import { getInfo } from '@/components/Grid/components/Table/formatters';
import TextTruncated from '@/components/TextTruncated';
import { formatDateTime } from 'utils';

export default function LogFormatter({ info }) {
  const { collapseId, setCollapseId } = useCollapse()
  const { row } = getInfo(info)

  const { document_id: id, source, level, tags, data } = row
  const { message, meta } = data || {}

  return <>
    <Collapsible onOpenChange={open => setCollapseId(open ? id : null)} open={collapseId === id}>
      <CollapsibleTrigger asChild>
        <div className='flex items-center gap-2'>
          <Badge size="md" variant="outline" color="accent">{SOURCE_MAPPING[source].toLowerCase()}</Badge>
          <TextTruncated maxLength={120}>{capitalize(message)}</TextTruncated>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent asChild>
        <Row className="pl-2 pt-3">
          <Col size={1}>
            <Label>Level</Label>
            <Badge variant='light' size={'sm'} color={COLORS[level]}>{level}</Badge>
          </Col>
          <Col size={11}>
            <Label>Message</Label>
            {capitalize(message)}
          </Col>
          <Col size={12}>
            <Label>Data</Label>
            <CodeEditor
              className='twy-border-base border'
              height="250px"
              language="json"
              value={parseMeta(meta)}
            />
          </Col>
          <Col size={12}>
            <Label>Tags</Label>
            <div className='flex flex-wrap gap-2'>
              {(tags || []).map(tag => <Badge key={tag} size='sm' variant={'outline'} color="accent">{tag}</Badge>)}
            </div>
          </Col>
        </Row>
      </CollapsibleContent>
    </Collapsible>
  </>
}

export function DateFormatter({ info }) {
  const { row } = getInfo(info)
  const { collapseId, setCollapseId } = useCollapse()

  const { document_id: id, date } = row

  return <div className='flex gap-2 cursor-pointer' onClick={() => setCollapseId(collapseId === id ? null : id)}>
    <ButtonIcon>
      {collapseId === id ? <MinusCircle size={16} /> : <PlusCircle size={16} />}
    </ButtonIcon>
    {formatDateTime(parseInt(date))}
  </div>
}

function parseMeta(meta) {
  try {
    return JSON.stringify(JSON.parse(meta) || {}, null, 2)
  } catch (error) {
    return meta
  }
}

const SOURCE_OPTIONS = [
  { label: 'Path Ping Post', value: 'path-ping-post' },
  { label: 'Ping check', value: 'preping' },
  { label: 'Conversion Pixel', value: 'conversion-pixel' },
  { label: 'Registration Call', value: 'lmain' },
  { label: 'Postback', value: 'postback' },
  { label: 'Registration Status', value: 'registration-status' },
  { label: 'Path Calls', value: 'path-call' },
  { label: 'Page Calls', value: 'page-call' },
  { label: 'Pixel Calls', value: 'pixel-call' },
  { label: 'User Profile', value: 'user-profile' },
  { label: 'TCPA Call to Twyne', value: 'tcpa-preping' },
  { label: 'TCPA Page Answer', value: 'tcpa-answer' },
  { label: 'Unsubscribe', value: 'unsubscribe' },
  { label: 'Blacklist Block', value: 'blacklist-block' },
  { label: 'Wildcard Redirect', value: 'wildcard-redirect' },
  { label: 'Verification Throttle', value: 'verification-throttle' },
  { label: "Rewards", value: "rewards" },
  { label: "Recaptcha Error", value: "recaptcha-error" },
  { label: "Path preflight", value: "path-preflight" },
  { label: 'Custom Conversion', value: 'custom-conversion' },
]

const SOURCE_MAPPING = SOURCE_OPTIONS.reduce((total, { value, label }) => {
  total[`${value}`] = label
  return total
}, {})

const COLORS = {
  info: 'neutral',
  warn: 'warning',
  error: 'danger',
}
